import React, { useState, useEffect } from "react";
import { usageAPI, tokensAPI } from "../services/api";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Clock,
  DollarSign,
  Download,
  Filter,
  Activity,
  Waves,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const Usage = () => {
  const [usageHistory, setUsageHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    serviceType: "",
    tokenId: "",
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    fetchUsageData();
  }, [filters]);

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      const tokensResult = await tokensAPI.list();
      setTokens(tokensResult.tokens || tokensResult || []);
    } catch (error) {
      console.error("Failed to fetch tokens:", error);
    }
  };

  const fetchUsageData = async () => {
    try {
      setLoading(true);
      const [historyResult, analyticsResult] = await Promise.all([
        usageAPI.getHistory(filters),
        usageAPI.getAnalytics({
          period: "month",
          days: 30,
          serviceType: filters.serviceType,
          tokenId: filters.tokenId,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
      ]);

      setUsageHistory(historyResult);
      setAnalytics(analyticsResult);
    } catch (error) {
      console.error("Failed to fetch usage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount);
  };

  const formatSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${Math.floor(seconds % 60)}s`;
  };

  const formatDate = (dateString) => {
    const locale = `${i18n.resolvedLanguage || "en"}-CH`;
    return new Date(dateString).toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getServiceTypeColor = (serviceType) => {
    const colors = {
      transcription: "#3b82f6",
      diarization: "#10b981",
      streaming: "#f59e0b",
    };
    return colors[serviceType] || "#6b7280";
  };

  const formatServiceType = (serviceType) => {
    return t(`usage.serviceTypes.${serviceType}`, serviceType);
  };

  const COLORS = ["#06b6d4", "#818cf8", "#f472b6", "#facc15"];

  const totalEvents = analytics?.serviceBreakdown?.reduce(
    (sum, service) => sum + service.events,
    0,
  );
  const totalSeconds = analytics?.serviceBreakdown?.reduce(
    (sum, service) => sum + service.audioSeconds,
    0,
  );
  const totalCost = analytics?.serviceBreakdown?.reduce(
    (sum, service) => sum + service.cost,
    0,
  );
  const avgProcessingSeconds = usageHistory.summary?.avgProcessingTime
    ? Math.round(usageHistory.summary.avgProcessingTime / 1000)
    : null;

  if (loading && !usageHistory.usage) {
    return (
      <div className="min-h-[60vh] bg-[#020617] text-white flex items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#030816] text-white">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="absolute -top-10 left-1/3 h-80 w-80 rounded-full bg-cyan-500/20 blur-[140px]" />
        <div className="absolute bottom-[-10%] right-0 h-96 w-96 rounded-full bg-indigo-500/20 blur-[140px]" />
        <div className="absolute inset-0 opacity-20">
          <div className="h-full w-full [background:radial-gradient(circle_at_1px,_rgba(255,255,255,0.12)_1px,_transparent_0)] [background-size:34px_34px]" />
        </div>
      </div>

      <div className="relative z-10 px-4 pt-6 pb-12 sm:px-8 lg:px-12 space-y-8">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl shadow-cyan-500/10">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-white">
                {t("usage.title")}
              </h1>
              <p className="mt-2 text-sm text-slate-300">
                {t("usage.subtitle")}
              </p>
            </div>
            <button className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 transition hover:bg-white/10">
              <Download className="h-4 w-4" />
              {t("usage.exportData")}
            </button>
          </div>

          {analytics && (
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
              {[
                {
                  label: t("usage.totalEvents"),
                  value: totalEvents || 0,
                  icon: Activity,
                },
                {
                  label: t("usage.audioProcessed"),
                  value: formatSeconds(totalSeconds || 0),
                  icon: Waves,
                },
                {
                  label: t("usage.totalCost"),
                  value: formatCurrency(totalCost || 0),
                  icon: DollarSign,
                },
                {
                  label: t("usage.avgProcessing"),
                  value: avgProcessingSeconds
                    ? `${avgProcessingSeconds}s`
                    : t("usage.notAvailable"),
                  icon: Clock,
                },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-2xl border border-white/10 bg-slate-950/40 p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                        {stat.label}
                      </p>
                      <p className="mt-3 text-2xl font-semibold text-white">
                        {stat.value}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
                      <stat.icon className="h-5 w-5 text-cyan-200" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {analytics && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                    {t("usage.serviceDistribution")}
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold text-white">
                    {t("usage.last30days")}
                  </h3>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={analytics.serviceBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="cost"
                    nameKey="serviceType"
                  >
                    {analytics.serviceBreakdown.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [
                      formatCurrency(value),
                      formatServiceType(name),
                    ]}
                    contentStyle={{
                      backgroundColor: "#0f172a",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: "0.75rem",
                      color: "#ffffff",
                      padding: "12px",
                    }}
                    labelStyle={{
                      color: "#e2e8f0",
                      fontWeight: "600",
                      marginBottom: "4px",
                    }}
                    itemStyle={{
                      color: "#ffffff",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {analytics.serviceBreakdown.map((service) => (
                  <div
                    key={service.serviceType}
                    className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white"
                  >
                    <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                      {formatServiceType(service.serviceType)}
                    </p>
                    <p className="mt-1 text-lg font-semibold">
                      {formatCurrency(service.cost)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {service.events} {t("usage.events")} ·{" "}
                      {formatSeconds(service.audioSeconds)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                    {t("usage.dailyTrend")}
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold text-white">
                    {t("usage.last14daysCost")}
                  </h3>
                </div>
              </div>
              <div className="mt-4 h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analytics.timeSeries?.slice(-14) || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                    <XAxis
                      dataKey="period.day"
                      stroke="#94a3b8"
                      tickFormatter={(value, index, payload) => {
                        const period = payload?.payload?.period;
                        if (!period) return value;
                        return `${String(period.month).padStart(2, "0")}/${
                          period.day
                        }`;
                      }}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tickFormatter={(value) => `${value}`}
                    ></YAxis>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#0f172a",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "0.75rem",
                        color: "#ffffff",
                        padding: "12px",
                      }}
                      labelStyle={{
                        color: "#e2e8f0",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                      itemStyle={{
                        color: "#ffffff",
                      }}
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(_, payload) => {
                        const period = payload?.[0]?.payload?.period;
                        if (!period) return "";
                        return `${period.year}-${String(period.month).padStart(
                          2,
                          "0",
                        )}-${String(period.day).padStart(2, "0")}`;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="cost"
                      stroke="#38bdf8"
                      strokeWidth={3}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/5">
                <Filter className="h-5 w-5 text-white/70" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                  {t("usage.filters")}
                </p>
                <p className="text-sm text-slate-300">
                  {t("usage.filtersDesc")}
                </p>
              </div>
            </div>

            <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-5">
              <select
                value={filters.serviceType}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    serviceType: e.target.value,
                    page: 1,
                  })
                }
                className="rounded-2xl border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-white focus:border-cyan-400/70 focus:outline-none"
              >
                <option value="">{t("usage.allServices")}</option>
                <option value="transcription_live">{t("usage.live")}</option>
                <option value="transcription_file_sync">
                  {t("usage.fileSync")}
                </option>
                <option value="transcription_file_async">
                  {t("usage.fileAsync")}
                </option>
                <option value="llm_chat">{t("usage.llmChat")}</option>
              </select>

              <select
                value={filters.tokenId}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    tokenId: e.target.value,
                    page: 1,
                  })
                }
                className="rounded-2xl border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-white focus:border-cyan-400/70 focus:outline-none"
              >
                <option value="">{t("usage.allTokens")}</option>
                {tokens.map((token) => (
                  <option
                    key={token.id || token._id}
                    value={token.id || token._id}
                  >
                    {token.name}
                  </option>
                ))}
              </select>

              <input
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value, page: 1 })
                }
                className="rounded-2xl border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-white focus:border-cyan-400/70 focus:outline-none"
              />

              <input
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value, page: 1 })
                }
                className="rounded-2xl border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-white focus:border-cyan-400/70 focus:outline-none"
              />

              <select
                value={filters.limit}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    limit: parseInt(e.target.value, 10),
                    page: 1,
                  })
                }
                className="rounded-2xl border border-white/10 bg-slate-950/50 px-3 py-2 text-sm text-white focus:border-cyan-400/70 focus:outline-none"
              >
                <option value="20">{t("usage.perPage", { count: 20 })}</option>
                <option value="50">{t("usage.perPage", { count: 50 })}</option>
                <option value="100">
                  {t("usage.perPage", { count: 100 })}
                </option>
              </select>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-black/30">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-cyan-200/70">
                {t("usage.usageLedger")}
              </p>
              <h3 className="text-2xl font-semibold text-white">
                {t("usage.eventHistory")}
              </h3>
            </div>
          </div>

          {usageHistory.usage && usageHistory.usage.length > 0 ? (
            <>
              <div className="mt-6 overflow-x-auto rounded-2xl border border-white/5">
                <table className="min-w-full divide-y divide-white/10 text-sm">
                  <thead className="bg-white/5 text-left text-xs uppercase tracking-[0.3em] text-slate-400">
                    <tr>
                      <th className="px-6 py-3">{t("usage.service")}</th>
                      <th className="px-6 py-3">{t("usage.token")}</th>
                      <th className="px-6 py-3">{t("usage.audio")}</th>
                      <th className="px-6 py-3">{t("usage.cost")}</th>
                      <th className="px-6 py-3">{t("usage.processing")}</th>
                      <th className="px-6 py-3">{t("usage.completed")}</th>
                      <th className="px-6 py-3">{t("usage.status")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 bg-white/5/10 text-white">
                    {usageHistory.usage.map((event) => (
                      <tr key={event.id} className="backdrop-blur">
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-3">
                              <span
                                className={`h-2.5 w-2.5 rounded-full ${
                                  event.serviceType === "transcription_live"
                                    ? "bg-amber-500"
                                    : event.serviceType === "llm_chat"
                                      ? "bg-purple-500"
                                      : "bg-blue-500"
                                }`}
                              />
                              <span className="font-medium capitalize">
                                {formatServiceType(event.serviceType)}
                              </span>
                            </div>
                            {event.diarizationEnabled && (
                              <span className="ml-6 text-xs text-slate-400">
                                {t("usage.diarizationPlus")}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-200">
                          {event.token ? (
                            <div className="flex flex-col gap-1">
                              <span className="font-medium">
                                {event.token.name}
                              </span>
                              {event.token.prefix && (
                                <span className="text-xs text-slate-400">
                                  {event.token.prefix}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400">
                              {t("usage.notAvailable")}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-slate-200">
                          {formatSeconds(event.audioSeconds)}
                        </td>
                        <td className="px-6 py-4 text-slate-200">
                          {formatCurrency(event.totalCost)}
                        </td>
                        <td className="px-6 py-4 text-slate-200">
                          {event.processingTimeMs > 0
                            ? `${Math.round(event.processingTimeMs / 1000)}s`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 text-slate-200">
                          {formatDate(event.completedAt)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              event.status === "billed"
                                ? "bg-emerald-400/10 text-emerald-300"
                                : event.status === "pending"
                                  ? "bg-amber-400/10 text-amber-300"
                                  : "bg-rose-400/10 text-rose-300"
                            }`}
                          >
                            {event.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {usageHistory.pagination && usageHistory.pagination.total > 1 && (
                <div className="mt-6 flex flex-col gap-3 text-sm text-slate-300 md:flex-row md:items-center md:justify-between">
                  <p>
                    {t("usage.showing")}{" "}
                    {(usageHistory.pagination.current - 1) * filters.limit + 1}–
                    {Math.min(
                      usageHistory.pagination.current * filters.limit,
                      usageHistory.pagination.totalItems,
                    )}{" "}
                    {t("usage.of")} {usageHistory.pagination.totalItems}{" "}
                    {t("usage.records")}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setFilters({ ...filters, page: filters.page - 1 })
                      }
                      disabled={filters.page <= 1}
                      className="rounded-2xl border border-white/10 px-4 py-2 text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {t("usage.previous")}
                    </button>
                    <button
                      onClick={() =>
                        setFilters({ ...filters, page: filters.page + 1 })
                      }
                      disabled={filters.page >= usageHistory.pagination.total}
                      className="rounded-2xl border border-white/10 px-4 py-2 text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {t("usage.next")}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-white/20 bg-white/5">
                <BarChart3 className="h-8 w-8 text-white/70" />
              </div>
              <h3 className="mt-6 text-2xl font-semibold">
                {t("usage.noUsage")}
              </h3>
              <p className="mt-2 max-w-md text-sm text-slate-300">
                {t("usage.noUsageDesc")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Usage;
